import { useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Archive, Building2, ExternalLink, GitMerge, ListPlus, Pencil, RotateCcw, User, X } from 'lucide-react'
import clsx from 'clsx'

import { crmErrorDetail, crmService, type CrmContact } from '@/services/crm'
import {
  ActivitySection,
  BrokerIntelligenceSection,
  CommunicationsSection,
  DealsSection,
  EnrichmentSection,
  NewsSection,
  NotesSection,
  ReachSection,
  SponsorFinancialsSection,
} from './ProfileSections'
import { contactInitials, matchesSearch, NOT_ON_RECORD, scoreBg, scoreColor, typeBadgeColor } from './crmFormat'
import { ActionMenu, MenuConfirm, MenuItem } from './ActionMenu'
import AddToListPopover from './AddToListPopover'
import { OrganizationSection } from './OrganizationPeople'
import { locationText } from './profileFacts'

/**
 * The side panel: what a single click on a row opens.
 *
 * It reads one contact, in the order a desk wants it — how to reach them,
 * the deals they were on, the messages, what lookup has found, then the
 * type-specific intelligence, the notes and the trail. The sections are the
 * same components the full window uses, so the two views cannot disagree
 * about a fact.
 *
 * Every action a person takes on a contact starts here or on the row:
 * confirm a captured row, retire (never delete), restore, edit, open the
 * whole file, put on a list, or fold a duplicate into the record that should
 * survive. Results and failures are said next to the control that caused
 * them, and they belong to THIS contact — the old page kept one page-wide
 * note that survived selecting somebody else.
 */
export default function ContactProfilePanel({
  contact,
  contacts,
  onClose,
  onOpenWindow,
  onEdit,
  onRetired,
  onSelectContact,
}: {
  contact: CrmContact
  /** The directory, for the merge picker and the company link. */
  contacts: CrmContact[]
  onClose: () => void
  onOpenWindow: (id: string) => void
  onEdit: (contact: CrmContact) => void
  /** After a retire, restore or merge landed: the row this panel showed is
   *  no longer the one to show. */
  onRetired?: (id: string) => void
  onSelectContact?: (id: string) => void
}) {
  const queryClient = useQueryClient()
  const [note, setNote] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null)

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['crm', 'contacts'] }),
      queryClient.invalidateQueries({ queryKey: ['crm', 'lists'] }),
      queryClient.invalidateQueries({ queryKey: ['crm', 'activity'] }),
      queryClient.invalidateQueries({ queryKey: ['crm', 'duplicates'] }),
    ])

  const confirm = useMutation({
    mutationFn: () => crmService.confirmContact(contact.id),
    onSuccess: async () => {
      setNote({ tone: 'ok', text: 'Marked checked.' })
      await refresh()
    },
    onError: (failure) => setNote({ tone: 'error', text: crmErrorDetail(failure, 'The contact could not be marked checked.') }),
  })

  const retire = useMutation({
    mutationFn: () => crmService.archiveContact(contact.id),
    onSuccess: async () => {
      await refresh()
      onRetired?.(contact.id)
    },
    onError: (failure) => setNote({ tone: 'error', text: crmErrorDetail(failure, 'The contact could not be retired.') }),
  })

  const restore = useMutation({
    mutationFn: () => crmService.restoreContact(contact.id),
    onSuccess: async () => {
      setNote({ tone: 'ok', text: 'Restored.' })
      await refresh()
      onRetired?.(contact.id)
    },
    onError: (failure) => setNote({ tone: 'error', text: crmErrorDetail(failure, 'The contact could not be restored.') }),
  })

  const merge = useMutation({
    mutationFn: (into: string) => crmService.mergeContact(contact.id, into),
    onSuccess: async (_result, into) => {
      await refresh()
      // The survivor is the record to be looking at now.
      onSelectContact?.(into)
    },
    onError: (failure) => setNote({ tone: 'error', text: crmErrorDetail(failure, 'The contacts could not be merged.') }),
  })

  const entity = contact.kind === 'organisation' || contact.isEntity
  const retired = Boolean(contact.retired)
  const isBroker = contact.companyType === 'Broker'
  const isSponsorMaster = contact.id.startsWith('spn-')
  // Only a captured row can be folded into another record; the masters have
  // their own merge routes on their own screens.
  const canMerge = contact.id.startsWith('dc-')
  const companyContact = useMemo(
    () => (contact.companyId ? contacts.find((c) => c.id === contact.companyId) ?? null : null),
    [contacts, contact.companyId],
  )

  return (
    <aside
      aria-label="Contact profile"
      className="flex w-[42%] min-w-0 flex-col border-l border-amber-400/10 bg-black/10"
    >
      {/* Header */}
      <div className="flex-shrink-0 border-b border-amber-400/10 bg-black/20 px-4 pb-3 pt-3">
        <div className="flex items-start gap-3">
          <div
            className={clsx(
              'flex h-12 w-12 flex-shrink-0 items-center justify-center border border-amber-400/20 bg-amber-400/[0.08] text-sm font-bold text-amber-300',
              entity ? 'rounded-lg' : 'rounded-full',
            )}
            aria-hidden="true"
          >
            {entity ? <Building2 size={18} /> : contactInitials(contact)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <h2
                className={clsx(
                  'min-w-0 truncate text-[17px] font-semibold leading-tight',
                  retired ? 'text-stone-400 line-through' : 'text-stone-100',
                )}
              >
                {contact.displayName}
              </h2>
              <span className={clsx('rounded-full border px-2 py-0.5 text-[9px] font-medium', typeBadgeColor(contact.companyType))}>
                {contact.companyType}
              </span>
              <span
                className="text-stone-500"
                title={entity ? 'An organization' : 'A person'}
                aria-label={entity ? 'An organization' : 'A person'}
                role="img"
              >
                {entity ? <Building2 size={11} /> : <User size={11} />}
              </span>
              {contact.isInternal ? (
                <span className="rounded-full border border-stone-500/30 px-1.5 py-0.5 text-[9px] text-stone-400">Internal</span>
              ) : null}
              {contact.needsReview && !retired ? (
                <span
                  className="flex items-center gap-1 rounded-full border border-amber-400/25 bg-amber-400/[0.08] px-1.5 py-0.5 text-[9px] text-amber-200"
                  title={contact.reviewReason ? `Unsure: ${contact.reviewReason}` : 'The engine is unsure of this entry'}
                >
                  Unsure
                  <button
                    type="button"
                    onClick={() => confirm.mutate()}
                    disabled={confirm.isPending}
                    className="rounded border border-amber-400/30 px-1 text-[9px] font-medium hover:bg-amber-400/[0.12] disabled:text-stone-600"
                    title="The record is right as it stands"
                  >
                    {confirm.isPending ? 'Saving…' : 'Mark checked'}
                  </button>
                </span>
              ) : null}
              {retired ? (
                <span className="flex items-center gap-1 rounded-full border border-stone-500/30 px-1.5 py-0.5 text-[9px] text-stone-400">
                  Retired
                  <button
                    type="button"
                    onClick={() => restore.mutate()}
                    disabled={restore.isPending}
                    className="rounded border border-stone-500/40 px-1 text-[9px] font-medium text-stone-200 hover:bg-stone-500/20 disabled:text-stone-600"
                  >
                    {restore.isPending ? 'Restoring…' : 'Restore'}
                  </button>
                </span>
              ) : null}
            </div>
            {/* One line — title · company · location — that truncates as a
                whole rather than wrapping a lone "Not on record" under the
                rest, orphaned from its separator. */}
            <div
              className="mt-0.5 min-w-0 truncate text-[12px] text-stone-400"
              title={[contact.title, contact.company, locationText(contact)].filter(Boolean).join(' · ')}
            >
              <span>{contact.title ?? NOT_ON_RECORD}</span>
              <span className="mx-1.5 text-stone-700">·</span>
              {companyContact && onSelectContact ? (
                <button
                  type="button"
                  onClick={() => onSelectContact(companyContact.id)}
                  className="text-stone-300 underline decoration-dotted underline-offset-2 hover:text-amber-200"
                  title="Open the company's own record"
                >
                  {contact.company ?? companyContact.displayName}
                </button>
              ) : (
                <span>{contact.company ?? NOT_ON_RECORD}</span>
              )}
              <span className="mx-1.5 text-stone-700">·</span>
              <span>{locationText(contact) ?? NOT_ON_RECORD}</span>
            </div>
          </div>
          <span
            data-testid="crm-score"
            title="Relationship score"
            className={clsx(
              'flex h-8 w-10 flex-shrink-0 items-center justify-center rounded-lg border text-[13px] font-bold',
              scoreBg(contact.score),
              scoreColor(contact.score),
            )}
          >
            {contact.score ?? '—'}
          </span>
        </div>

        {/* Actions */}
        <div className="mt-2.5 flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => onOpenWindow(contact.id)}
            className="flex items-center gap-1 rounded-md border border-amber-400/20 bg-amber-400/[0.06] px-2 py-1 text-[10px] font-medium text-amber-300 hover:bg-amber-400/[0.12]"
            title="Open the full contact"
          >
            <ExternalLink size={10} /> Open
          </button>
          <button
            type="button"
            onClick={() => onEdit(contact)}
            className="flex items-center gap-1 rounded-md border border-amber-400/10 bg-black/40 px-2 py-1 text-[10px] font-medium text-stone-300 hover:text-amber-200"
          >
            <Pencil size={10} /> Edit
          </button>
          <ActionMenu
            label={`More actions for ${contact.displayName}`}
            className="rounded-md border border-amber-400/10 bg-black/40 px-2 py-1 text-stone-300 hover:text-amber-200"
          >
            {(close) => (
              <PanelMenu
                contact={contact}
                contacts={contacts}
                canMerge={canMerge}
                retiring={retire.isPending}
                merging={merge.isPending}
                onRetire={() => retire.mutate(undefined, { onSuccess: close })}
                onRestore={() => restore.mutate(undefined, { onSuccess: close })}
                onMerge={(into) => merge.mutate(into, { onSuccess: close })}
              />
            )}
          </ActionMenu>
          <span className="flex-1" />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close profile"
            className="rounded p-1 text-stone-500 hover:text-stone-200 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60"
          >
            <X size={14} />
          </button>
        </div>
        {note ? (
          <div className={clsx('mt-1.5 text-[10px]', note.tone === 'error' ? 'text-red-300' : 'text-stone-400')} role="status">
            {note.text}
          </div>
        ) : null}
      </div>

      {/* Sections — the same renderers the full window uses. */}
      <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin">
        <ReachSection contact={contact} dense />
        {entity || contact.company ? (
          <OrganizationSection
            key={contact.id}
            title={entity ? 'People under this organization' : `People at ${contact.company}`}
            name={entity ? contact.displayName : (contact.company as string)}
            focusId={entity ? null : contact.id}
            onOpenContact={onSelectContact}
          />
        ) : null}
        <DealsSection contact={contact} />
        <CommunicationsSection contact={contact} />
        <EnrichmentSection contact={contact} />
        <NewsSection contact={contact} />
        {isBroker ? <BrokerIntelligenceSection contact={contact} /> : null}
        {isSponsorMaster ? <SponsorFinancialsSection contact={contact} /> : null}
        <NotesSection contact={contact} />
        <ActivitySection contactId={contact.id} limit={8} />
      </div>
    </aside>
  )
}

/** The panel's ⋯: put on a list, merge into another record, retire. */
function PanelMenu({
  contact,
  contacts,
  canMerge,
  retiring,
  merging,
  onRetire,
  onRestore,
  onMerge,
}: {
  contact: CrmContact
  contacts: CrmContact[]
  canMerge: boolean
  retiring: boolean
  merging: boolean
  onRetire: () => void
  onRestore: () => void
  onMerge: (into: string) => void
}) {
  const [mode, setMode] = useState<'items' | 'lists' | 'merge' | 'retire'>('items')
  const [query, setQuery] = useState('')
  const [target, setTarget] = useState<CrmContact | null>(null)

  if (mode === 'lists') {
    return <AddToListPopover contactId={contact.id} contactName={contact.displayName} />
  }
  if (mode === 'retire') {
    return (
      <MenuConfirm
        question={`Retire ${contact.displayName}?`}
        confirmLabel="Yes, retire"
        pending={retiring}
        onConfirm={onRetire}
        onCancel={() => setMode('items')}
      />
    )
  }
  if (mode === 'merge') {
    if (target) {
      return (
        <MenuConfirm
          question={`Merge ${contact.displayName} into ${target.displayName}? This retires ${contact.displayName}.`}
          confirmLabel="Yes, merge"
          pending={merging}
          onConfirm={() => onMerge(target.id)}
          onCancel={() => setTarget(null)}
        />
      )
    }
    const q = query.trim()
    const candidates = q
      ? contacts.filter((c) => c.id !== contact.id && !c.retired && matchesSearch(c, q)).slice(0, 8)
      : []
    return (
      <div className="w-[260px] px-1 py-0.5">
        <div className="px-1 pb-1 font-mono text-[8px] uppercase tracking-[0.18em] text-stone-600">
          Merge {contact.displayName} into…
        </div>
        <input
          autoFocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search the directory"
          aria-label="Contact to merge into"
          className="mb-1 w-full rounded border border-amber-400/15 bg-black/50 px-2 py-1 text-[11px] text-stone-200 outline-none placeholder:text-stone-600"
        />
        {q && candidates.length === 0 ? (
          <div className="px-1 py-1 text-[10px] text-stone-500">Nobody else matches.</div>
        ) : null}
        {candidates.map((candidate) => (
          <MenuItem key={candidate.id} onSelect={() => setTarget(candidate)}>
            <span className="flex items-center gap-2">
              <span className="min-w-0 flex-1 truncate">{candidate.displayName}</span>
              <span className="truncate text-[9px] text-stone-500">{candidate.company ?? candidate.email ?? ''}</span>
            </span>
          </MenuItem>
        ))}
        <button
          type="button"
          role="menuitem"
          onClick={() => setMode('items')}
          className="mt-1 w-full rounded px-2 py-1 text-left text-[10px] text-stone-500 hover:text-stone-300"
        >
          ← Back
        </button>
      </div>
    )
  }
  return (
    <>
      <MenuItem icon={<ListPlus size={11} />} onSelect={() => setMode('lists')}>
        Add to list…
      </MenuItem>
      <MenuItem
        icon={<GitMerge size={11} />}
        onSelect={() => setMode('merge')}
        disabled={!canMerge}
        hint="Only a contact captured off the deals can be merged here"
      >
        Merge into…
      </MenuItem>
      {contact.retired ? (
        <MenuItem icon={<RotateCcw size={11} />} onSelect={onRestore}>
          Restore
        </MenuItem>
      ) : (
        <MenuItem icon={<Archive size={11} />} danger onSelect={() => setMode('retire')}>
          Retire…
        </MenuItem>
      )}
    </>
  )
}
